import React, { useState, useEffect, useRef } from 'react';
import { FaWifi } from 'react-icons/fa';

const NetworkPingWidget = () => {
    const [ping, setPing] = useState({ ms: 0, status: 'good' });
    const [position, setPosition] = useState({ 
        x: Math.max(16, window.innerWidth - 170), 
        y: Math.max(16, window.innerHeight - 56) 
    });
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const isMountedRef = useRef(true);
    const timerRef = useRef(null);
    const failCountRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;

        const checkPing = async () => {
            const start = Date.now();
            try {
                await fetch('https://www.google.com/favicon.ico?_=' + start, { mode: 'no-cors', cache: 'no-store' });
                const delay = Date.now() - start;

                if (!isMountedRef.current) return;

                failCountRef.current = 0; // reset on success

                let status = 'good';
                if (delay > 200) status = 'fair';
                if (delay > 500) status = 'poor';
                setPing({ ms: delay, status });

                schedulePing(15000);
            } catch {
                if (!isMountedRef.current) return;

                failCountRef.current += 1;
                setPing({ ms: 999, status: 'offline' });

                const backoffMs = Math.min(15000 * Math.pow(2, failCountRef.current - 1), 120000);
                schedulePing(backoffMs);
            }
        };

        const schedulePing = (delay) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (!isMountedRef.current) return;
            timerRef.current = setTimeout(checkPing, delay);
        };

        checkPing();

        return () => {
            isMountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setPosition(prev => ({
                x: Math.min(prev.x, window.innerWidth - 170),
                y: Math.min(prev.y, window.innerHeight - 56)
            }));
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            setPosition({
                x: Math.max(8, Math.min(e.clientX - dragOffset.current.x, window.innerWidth - 170)),
                y: Math.max(8, Math.min(e.clientY - dragOffset.current.y, window.innerHeight - 56))
            });
        };

        const handleMouseUp = () => { isDragging.current = false; };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const handleMouseDown = (e) => {
        isDragging.current = true;
        dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    };

    const getColor = (status) => {
        if (status === 'good') return '#4ade80';
        if (status === 'fair') return '#facc15';
        if (status === 'offline') return '#94a3b8';
        return '#ef4444';
    };

    const getStatusText = (status) => {
        if (status === 'good') return 'Fast';
        if (status === 'fair') return 'Normal';
        if (status === 'offline') return 'Offline';
        return 'Slow';
    };

    return (
        <div
            onMouseDown={handleMouseDown}
            style={{
                position: 'fixed',
                left: position.x + 'px',
                top: position.y + 'px',
                backgroundColor: 'rgba(0, 28, 58, 0.9)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '0.72rem',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                zIndex: 9999,
                cursor: 'grab',
                userSelect: 'none'
            }}
        >
            <FaWifi color={getColor(ping.status)} size={14} />
            <span style={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Internet: <span style={{ color: getColor(ping.status) }}>{getStatusText(ping.status)}</span>
            </span>
        </div>
    );
};

export default NetworkPingWidget;
